import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useUserRole } from '../../hooks/useUserRole';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import {
  PEDIDO_POR_ID,
  CANCELAR_ITEM_PEDIDO,
  CANCELAR_PEDIDO,
  STATUS_PEDIDO_LABEL,
  formatCurrency,
} from '../../services/quadraService';

const useCountdown = (targetIso: string | null) => {
  const calc = useCallback(() => {
    if (!targetIso) return null;
    const diff = new Date(targetIso).getTime() - Date.now();
    if (diff <= 0) return null;
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, [targetIso]);

  const [remaining, setRemaining] = useState(calc);
  useEffect(() => {
    if (!targetIso) return;
    const interval = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(interval);
  }, [targetIso, calc]);
  return remaining;
};

const PedidoDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useUserRole();
  const [erro, setErro] = useState('');
  const [mostrarCancelamento, setMostrarCancelamento] = useState(false);

  const { data, loading, error, refetch } = useQuery(PEDIDO_POR_ID, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [cancelarItem, { loading: loadingCancelarItem }] = useMutation(CANCELAR_ITEM_PEDIDO);
  const [cancelarPedido, { loading: loadingCancelarPedido }] = useMutation(CANCELAR_PEDIDO);

  const pedido = data?.pedidoAgendamentoPorId;
  const countdown = useCountdown(
    pedido && ['ABERTO', 'AGUARDANDO_PAGAMENTO'].includes(pedido.status) ? pedido.reservaExpiraEm : null
  );

  const reservaExpirada = pedido && ['ABERTO', 'AGUARDANDO_PAGAMENTO'].includes(pedido.status) && pedido.reservaExpiraEm && !countdown;
  const podePagar = pedido && ['ABERTO', 'AGUARDANDO_PAGAMENTO'].includes(pedido.status) && !reservaExpirada;
  const podeCancelar = pedido && ['ABERTO', 'AGUARDANDO_PAGAMENTO', 'PAGO'].includes(pedido.status);

  const itensAtivos = pedido?.agendamentos?.filter((a: any) => a.status !== 'CANCELADO') ?? [];

  const contarSlots = (horaInicio: string, horaFim: string, duracaoSlotMinutos: number) => {
    const [hI, mI] = horaInicio.split(':').map(Number);
    const [hF, mF] = horaFim.split(':').map(Number);
    const totalMin = (hF * 60 + mF) - (hI * 60 + mI);
    return Math.round(totalMin / duracaoSlotMinutos);
  };

  const totalSlots = itensAtivos.reduce((acc: number, a: any) =>
    acc + contarSlots(a.horaInicio, a.horaFim, a.quadra?.duracaoSlotMinutos ?? 30), 0
  );

  const handleCancelarItem = async (agendamentoId: string) => {
    if (!window.confirm('Remover este horário do pedido?')) return;
    setErro('');
    try {
      await cancelarItem({ variables: { pedidoId: id, agendamentoId } });
      refetch();
    } catch (e: any) {
      setErro(e?.graphQLErrors?.[0]?.message ?? e.message ?? 'Erro ao remover item.');
    }
  };

  const handleCancelarPedido = async () => {
    setErro('');
    try {
      await cancelarPedido({ variables: { id } });
      navigate('/quadras/meus-agendamentos');
    } catch (e: any) {
      setErro(e?.graphQLErrors?.[0]?.message ?? e.message ?? 'Erro ao cancelar reserva.');
    }
  };

  // Calcula se haverá multa
  const calcularInfoCancelamento = () => {
    if (!pedido) return { temMulta: false, horasRestantes: 0, janela: null };
    const janela = pedido.local?.janelaHorasReembolso;
    if (janela == null || pedido.status !== 'PAGO') return { temMulta: false, horasRestantes: 0, janela };

    const itensAtivosCalc = pedido.agendamentos?.filter((a: any) => a.status !== 'CANCELADO') ?? [];
    if (itensAtivosCalc.length === 0) return { temMulta: false, horasRestantes: 0, janela };

    // Pega o primeiro agendamento (mais próximo)
    const primeiroItem = itensAtivosCalc.sort((a: any, b: any) =>
      (a.dataAgendamento + a.horaInicio).localeCompare(b.dataAgendamento + b.horaInicio)
    )[0];

    const dataHoraInicio = new Date(`${primeiroItem.dataAgendamento}T${primeiroItem.horaInicio}`);
    const agora = new Date();
    const horasRestantes = Math.floor((dataHoraInicio.getTime() - agora.getTime()) / (1000 * 60 * 60));

    return { temMulta: horasRestantes < janela, horasRestantes, janela };
  };

  const handlePagar = async () => {
    setErro('');
    navigate(`/quadras/pagamento-pedido/${pedido.id}`);
  };

  if (loading) return <Fragment><Header /><div className="main-content"><Load /></div><Appfooter /></Fragment>;
  if (error || !pedido) return (
    <Fragment><Header />
      <div className="main-content p-4">
        <div className="alert alert-danger font-xsss">{error?.graphQLErrors?.[0]?.message ?? 'Pedido não encontrado.'}</div>
      </div>
    <Appfooter /></Fragment>
  );

  const st = STATUS_PEDIDO_LABEL[pedido.status] ?? { label: pedido.status, color: 'secondary' };

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="d-flex align-items-center mb-3 gap-2">
              <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                <i className="feather-arrow-left me-1"></i> Voltar
              </button>
              <span className="font-xssss text-grey-500">Pedido #{pedido.id.slice(-8).toUpperCase()}</span>
            </div>

            <div className="row">
              <div className="col-xl-8 col-lg-7">

                {/* Header do pedido */}
                <div className="card shadow-xss rounded-xxl border-0 p-4 mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div>
                      <h2 className="fw-700 font-md text-grey-900 mb-1">
                        <i className="feather-shopping-cart me-2 text-primary"></i>
                        Reserva
                      </h2>
                      <span className="font-xssss text-grey-500">{pedido.local?.nome}</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      {pedido.agendamentos?.some((a: any) => a.tipoAtor === 'ADMIN') && (
                        <span className="badge bg-light text-grey-600 font-xssss rounded-xl">
                          Reservado pelo admin
                        </span>
                      )}
                      <span className={`badge bg-${st.color} font-xssss rounded-xl`}>{st.label}</span>
                    </div>
                  </div>

                  {/* Endereço do local */}
                  {pedido.local?.endereco && (
                    <div className="bg-greylight rounded-xl p-3 mb-2">
                      <p className="font-xsss text-grey-700 mb-1">
                        <i className="feather-map-pin me-1 text-primary"></i>
                        {[
                          pedido.local.endereco.logradouro,
                          pedido.local.endereco.numero,
                          pedido.local.endereco.complemento,
                        ].filter(Boolean).join(', ')}
                        {pedido.local.endereco.bairro ? ` — ${pedido.local.endereco.bairro}` : ''}
                      </p>
                      <p className="font-xssss text-grey-500 mb-1">
                        {pedido.local.endereco.cidade?.nome}{pedido.local.endereco.cidade?.uf ? ` - ${pedido.local.endereco.cidade.uf}` : ''}
                        {pedido.local.endereco.cep ? ` · CEP ${pedido.local.endereco.cep}` : ''}
                      </p>
                      {(() => {
                        const mapsUrl = pedido.local.urlLocalizacao
                          || (() => {
                            const end = pedido.local.endereco;
                            const parts = [end.logradouro, end.numero, end.bairro, end.cidade?.nome, end.cidade?.uf].filter(Boolean);
                            return parts.length > 0 ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}` : null;
                          })();
                        return mapsUrl ? (
                          <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary rounded-xl font-xssss fw-600 mt-1"
                          >
                            <i className="feather-navigation me-1"></i> Abrir no Google Maps
                          </a>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Countdown */}
                  {countdown && (
                    <div className="alert alert-warning font-xssss py-2 mb-0 d-flex align-items-center gap-2">
                      <i className="feather-clock"></i>
                      Reserva expira em <strong>{countdown}</strong> — conclua o pagamento para garantir os horários.
                    </div>
                  )}
                  {reservaExpirada && (
                    <div className="alert alert-danger font-xssss py-2 mb-0">
                      <i className="feather-alert-circle me-1"></i>
                      Reserva expirada por falta de pagamento.
                    </div>
                  )}
                </div>

                {/* Lista de itens */}
                <div className="card shadow-xss rounded-xxl border-0 mb-3">
                  <div className="p-4 border-bottom">
                    <h5 className="fw-700 font-xss text-grey-900 mb-0">Horários reservados</h5>
                  </div>
                  {pedido.agendamentos.map((ag: any) => {
                    const cancelado = ag.status === 'CANCELADO';
                    const dataFmt = new Date(ag.dataAgendamento + 'T12:00:00').toLocaleDateString('pt-BR', {
                      weekday: 'short', day: '2-digit', month: 'short',
                    });
                    return (
                      <div
                        key={ag.id}
                        className={`d-flex align-items-center justify-content-between p-3 border-bottom ${cancelado ? 'opacity-50' : ''}`}
                      >
                        <div>
                          <p className="fw-600 font-xsss text-grey-900 mb-0">{ag.quadra?.nome}</p>
                          <span className="font-xssss text-grey-500">
                            {dataFmt} · {ag.horaInicio} – {ag.horaFim}
                          </span>
                          {cancelado && (
                            <span className="badge bg-danger font-xssss rounded-xl ms-2">Cancelado</span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <div className="text-end">
                            {ag.valorOriginal != null && ag.valorOriginal !== ag.valor ? (
                              <>
                                <span className="font-xssss text-grey-400 text-decoration-line-through d-block">
                                  {formatCurrency(ag.valorOriginal)}
                                </span>
                                <span className="font-xsss fw-700 text-success d-block">
                                  {formatCurrency(ag.valor)}
                                </span>
                                <span className="badge bg-success font-xssss rounded-xl">
                                  -{ag.percentualDescontoAluno}% aluno
                                </span>
                              </>
                            ) : (
                              <span className="font-xsss fw-700 text-success">{formatCurrency(ag.valor)}</span>
                            )}
                          </div>
                          {!cancelado && podeCancelar && itensAtivos.length > 1 && isAdmin && (
                            <button
                              className="btn btn-sm btn-outline-danger rounded-xl font-xssss px-2 py-1"
                              onClick={() => handleCancelarItem(ag.id)}
                              disabled={loadingCancelarItem}
                              title="Remover este horário"
                            >
                              <i className="feather-x"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {erro && <div className="alert alert-danger font-xssss mb-3">{erro}</div>}

              </div>

              {/* Coluna lateral */}
              <div className="col-xl-4 col-lg-5">
                <div className="card shadow-xss rounded-xxl border-0 p-4 sticky-top" style={{ top: 80 }}>
                  <h4 className="fw-700 font-xss mb-3 text-grey-900">Resumo</h4>

                  <div className="bg-greylight rounded-xl p-3 mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="font-xssss text-grey-500">Local</span>
                      <span className="font-xssss fw-600">{pedido.local?.nome}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="font-xssss text-grey-500">Itens</span>
                      <span className="font-xssss fw-600">{totalSlots} horário{totalSlots !== 1 ? 's' : ''}</span>
                    </div>
                    {(() => {
                      const totalOriginal = itensAtivos.reduce((acc: number, a: any) => acc + (a.valorOriginal ?? a.valor ?? 0), 0);
                      const totalFinal = itensAtivos.reduce((acc: number, a: any) => acc + (a.valor ?? 0), 0);
                      const temDesconto = itensAtivos.some((a: any) => a.valorOriginal != null && a.valorOriginal !== a.valor);
                      return temDesconto ? (
                        <>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="font-xssss text-grey-500">Subtotal</span>
                            <span className="font-xssss text-grey-400 text-decoration-line-through">{formatCurrency(totalOriginal)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="font-xssss text-success fw-600">Desconto aluno</span>
                            <span className="font-xssss text-success fw-600">-{formatCurrency(totalOriginal - totalFinal)}</span>
                          </div>
                        </>
                      ) : null;
                    })()}
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between">
                      <span className="font-xsss fw-700">Total</span>
                      <span className="font-xsss fw-700 text-success">{formatCurrency(pedido.valorTotal)}</span>
                    </div>
                  </div>

                  {podePagar && (
                    <button
                      className="btn bg-primary-gradiant text-white rounded-xl fw-600 font-xsss w-100 mb-2"
                      onClick={handlePagar}
                    >
                      <i className="feather-credit-card me-1"></i>Pagar agora
                    </button>
                  )}

                  {podeCancelar && !mostrarCancelamento && (
                    <button
                      className="btn btn-outline-danger rounded-xl fw-600 font-xssss w-100 mb-2"
                      onClick={() => setMostrarCancelamento(true)}
                    >
                      Cancelar reserva
                    </button>
                  )}

                  {podeCancelar && mostrarCancelamento && (() => {
                    const { temMulta, horasRestantes, janela } = calcularInfoCancelamento();
                    return (
                      <div className="border border-danger rounded-xl p-3 mb-2">
                        <p className="fw-700 font-xssss text-danger mb-2">
                          <i className="feather-alert-triangle me-1"></i>Política de cancelamento
                        </p>

                        {pedido.status === 'PAGO' ? (
                          <>
                            {janela != null ? (
                              <>
                                <p className="font-xssss text-grey-600 mb-1">
                                  • Cancelamento com mais de <strong>{janela}h</strong> de antecedência: <span className="text-success fw-600">reembolso integral como crédito neste local</span>
                                </p>
                                <p className="font-xssss text-grey-600 mb-2">
                                  • Cancelamento com menos de <strong>{janela}h</strong> de antecedência: <span className="text-danger fw-600">sem reembolso (multa = valor total)</span>
                                </p>
                                <div className={`alert font-xssss py-2 mb-2 ${temMulta ? 'alert-danger' : 'alert-success'}`}>
                                  {temMulta ? (
                                    <>
                                      <i className="feather-x-circle me-1"></i>
                                      Faltam ~{horasRestantes}h para o horário. <strong>Você perderá o valor pago.</strong>
                                    </>
                                  ) : (
                                    <>
                                      <i className="feather-check-circle me-1"></i>
                                      Faltam ~{horasRestantes}h para o horário. <strong>Você receberá crédito para uso neste local.</strong>
                                    </>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="font-xssss text-grey-600 mb-2">
                                • Este local não possui política de multa. Você receberá <span className="text-success fw-600">crédito integral para uso neste local</span>.
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="font-xssss text-grey-600 mb-2">
                            • Os horários reservados serão liberados. Nenhuma cobrança será feita.
                          </p>
                        )}

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-danger rounded-xl fw-600 font-xssss flex-grow-1"
                            onClick={handleCancelarPedido}
                            disabled={loadingCancelarPedido}
                          >
                            {loadingCancelarPedido ? 'Cancelando...' : 'Confirmar cancelamento'}
                          </button>
                          <button
                            className="btn btn-light rounded-xl fw-600 font-xssss"
                            onClick={() => setMostrarCancelamento(false)}
                          >
                            Voltar
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  <button
                    className="btn btn-light rounded-xl fw-600 font-xssss w-100"
                    onClick={() => navigate('/quadras/meus-agendamentos')}
                  >
                    Meus agendamentos
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      <Appfooter />
    </Fragment>
  );
};

export default PedidoDetalhe;
